import { PORTS } from "../config/modulePort";
import api from "./Api";

const UserManagement = process.env.REACT_APP_USER_MGMT_URL || ""; // Default to an empty string or another default value

if (!UserManagement) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_AUTH_URL in your environment variables.",
  );
}
const Picklist = process.env.REACT_APP_PICKLIST_URL || ""; // Default to an empty string or another default value

if (!Picklist) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_AUTH_URL in your environment variables.",
  );
}

const picklistAPI = api(Picklist);
const userManagementAPI = api(UserManagement);

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

//Fetches the User list base on search
const fetchUserList = async (params: any): Promise<any> => {
  try {
    const response = await userManagementAPI.post("/api/user/search", params);

    // Assuming response.data?.result is an array of user objects
    const updatedResult = response.data?.result.map((user: any) => ({
      ...user,
      actions: { Edit: "Edit" }, // Add the actions field to each user
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error fetching the user list:", error);
    throw new Error("Unable to fetch the user list. Please try again later.");
  }
};

//Fetches the list of Picklist by group name for the dropdowns.
const fetchPickListByGroupName = async (params: any): Promise<any> => {
  try {
    const response = await picklistAPI.post(
      "/api/picklist/items/bygroupname",
      params,
    );
    // Map the result to transform pickListItemId into value and displayName into label
    const genderList = response.data?.result.map((item: any) => ({
      value: item.pickListItemId,
      label: item.displayName,
    }));

    return genderList;
  } catch (error) {
    console.error("Error fetching the data:", error);
    throw new Error("Unable to fetch the data. Please try again later.");
  }
};

//Fetches the list of companies for the dropdown.
const fetchCompanyIDList = async (): Promise<any> => {
  try {
    const response = await userManagementAPI.get("/api/user/companylist");
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching the company list:", error);
    throw new Error(
      "Unable to fetch the company list. Please try again later.",
    );
  }
};

//Fetches the user type based on the web roles.
const fetchUserTypeBaseOnWebRole = async (
  roleIds: number[] | number,
  TriggerById: any,
): Promise<any> => {
  try {
    const roleIdsParam = encodeURIComponent(
      Array.isArray(roleIds) ? roleIds.join(",") : roleIds,
    );
    const response = await userManagementAPI.get(
      `/api/user/roles/usertype?roleIds=${roleIdsParam}&TriggerById=${TriggerById}`,
    );
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching user type:", error);
    throw new Error(
      "Unable to fetch user types for the specified web role. Please try again later.",
    );
  }
};

//Fetches the Department based on the web roles.
const fetchDepartmenteBaseOnWebRole = async (
  roleIds: number[] | number,
  TriggerById: any,
): Promise<any> => {
  try {
    const roleIdsParam = encodeURIComponent(
      Array.isArray(roleIds) ? roleIds.join(",") : roleIds,
    );
    const response = await userManagementAPI.get(
      `/api/user/roles/department?roleIds=${roleIdsParam}&TriggerById=${TriggerById}`,
    );
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching department:", error);
    throw new Error(
      "Unable to fetch department for the specified web role. Please try again later.",
    );
  }
};

//Fetches the user form header info
const fetcheaderinfo = async (UserId: any): Promise<any> => {
  try {
    const response = await userManagementAPI.get(
      `/api/user/headerinfo?UserId=${UserId}`,
    );
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching web roles by department:", error);
    throw new Error(
      "Unable to fetch web roles for the specified department. Please try again later.",
    );
  }
};

//Fetches the user form device info
const fetcdeviceinfo = async (UserId: any): Promise<any> => {
  try {
    const response = await userManagementAPI.get(
      `/api/user/deviceinfo?UserId=${UserId}`,
    );
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching web roles by department:", error);
    throw new Error(
      "Unable to fetch web roles for the specified department. Please try again later.",
    );
  }
};

//Adds a new user.
const addUser = async (userData: any): Promise<ApiResponse<any>> => {
  try {
    const response = await userManagementAPI.post<ApiResponse<any>>(
      "/api/user/add",
      userData,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

//Update user details.
const updateUser = async (userData: any): Promise<ApiResponse<any>> => {
  try {
    const response = await userManagementAPI.put<ApiResponse<any>>(
      "/api/user/update",
      userData,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};
const validateUserPrefix = async (userPrefix: string): Promise<any> => {
  try {
    const response = await userManagementAPI.get(
      `/api/user/user-prefix/validate?userPrefix=${userPrefix}`,
    );
    return response.data?.result;
  } catch (error) {
    console.error("Error fetching department:", error);
    throw new Error(
      "Unable to fetch department for the specified web role. Please try again later.",
    );
  }
};

export {
  fetchUserList,
  fetchPickListByGroupName,
  fetchCompanyIDList,
  fetchUserTypeBaseOnWebRole,
  fetchDepartmenteBaseOnWebRole,
  fetcheaderinfo,
  fetcdeviceinfo,
  addUser,
  updateUser,
  validateUserPrefix,
};
